import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MainHome from "../../components/MainHome";
import "../../styles/components/UserProfile.scss";
import ButtonCustom from "../../components/common/Button";
import { useToast } from "../../common/contexts/useToast";
import { isValidPhoneNumber } from "../../common/validations/phoneNumber";
import CustomDropdown from "../../components/common/inputs/CustomDropdown";
import { Positions } from "../../components/common/general/positions";
import { FormBuilderInterface } from "../../components/common/FormBuilder";
import { updateUserProfile } from "../../common/api/apiCalls";
import useUserStore from "../../common/store/user.store";

const UserProfile = () => {
  const { user, updateUser, getUser } = useUserStore();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [userData, setUserData] = useState<any>({
    username: "",
    firstName: "",
    lastName: "",
    middleName: "",
    position: "",
    phoneNumber: null,
    password: "",
    fullName: "",
  });
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const translatedPositions = Positions.map((e) => t(e));

  useEffect(() => {
    getUser();
    getUserData();
  }, []);

  const getUserData = async () => {
    setUserData({
      username: user?.username,
      firstName: user?.firstName,
      lastName: user?.lastName,
      middleName: user?.middleName,
      position: user?.position,
      phoneNumber: user?.phoneNumber,
      password: "",
      fullName: user?.fullName,
    });
  };

  const buttonSection = [
    {
      label: t("First name"),
      value: userData.firstName,
      key: "firstName",
      inputType: "text",
    },
    {
      label: t("Middle name"),
      value: userData.middleName,
      key: "middleName",
      inputType: "text",
    },
    {
      label: t("Last name"),
      value: userData.lastName,
      key: "lastName",
      inputType: "text",
    },
    {
      label: t("Position"),
      value: userData.position,
      key: "position",
      inputType: "text",
    },
    {
      label: t("Phone number"),
      value: userData.phoneNumber,
      key: "phoneNumber",
      inputType: "phone",
    },
    {
      label: t("Change password"),
      value: "",
      key: "password",
      inputType: "password",
    },
  ];

  const handleEditClick = (key: string, currentValue: string) => {
    setEditingKey(key);
    setEditValue(currentValue);
  };

  const handleSaveClick = () => {
    let hasError = false;

    if (editingKey === "phoneNumber") {
      if (!isValidPhoneNumber(editValue)) {
        setErrors((prevErrors) => ({ ...prevErrors, phoneNumber: true }));
        showToast("error", "Error", t("general.errors.phoneNumberError"));
        hasError = true;
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, phoneNumber: false }));
      }
    }

    if (!hasError) {
      setUserData({ ...userData, [editingKey!]: editValue });
      setEditingKey(null);
      setIsEdit(true);
    }
  };

  const handleCancelClick = () => {
    setEditingKey(null);
    setEditValue("");
    setErrors({});
  };

  const saveUserProfile = async () => {
    const res = await updateUserProfile({
      firstName: userData.firstName,
      middleName: userData.middleName,
      lastName: userData.lastName,
      position: userData.position,
      phoneNumber: userData.phoneNumber,
      password: userData.password,
      username: userData.username,
    });
    if (!res.success) {
      showToast("error", "Error", t(res.data.errorMessage));
    } else {
      showToast("success", "Success", t("userProfile.userUpdated"));
      setIsEdit(false);
      const userDataToSet = {
        firstName: userData.firstName,
        middleName: userData.middleName,
        lastName: userData.lastName,
        position: userData.position,
        phoneNumber: userData.phoneNumber,
        username: userData.username,
        fullName: `${userData.firstName} ${userData.middleName} ${userData.lastName}`,
      }
      updateUser(userDataToSet);
      setUserData(userDataToSet);
      getUser();
    }
  };

  return (
    <MainHome>
      <div className="user-profile-container">
        <div className="header-section">
          <label className="heading-h2">{userData.fullName}</label>
          <span>{userData.username}</span>
        </div>
        <hr />

        <div className="body-section">
          {buttonSection.map((item, index) => (
            <div key={item.key + index}>
              {item.key === "password" ? (
                <>
                  <div>
                    <label className="heading-h6">{item.label}</label>
                  </div>
                  <button
                    onClick={() => navigate("/ForgetPassword", { state: { fromSettings: true } })}
                    className="btn btn-link"
                  >
                    {t("Change password")}
                  </button>
                </>
              ) : editingKey === item.key ? (
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="w-75">
                      <label className="form-label">{item.label}</label>
                      {item.key === "position" ? (
                        <div className="custom-user-profile-dropdown">
                          <CustomDropdown
                            name={item.key}
                            optionsArray={translatedPositions}
                            value={editValue}
                            handleInputChange={(e) =>
                              setEditValue(e.target.value)
                            }
                          />
                        </div>
                      ) : (
                        <input
                          type={item.inputType}
                          className={`form-control input ${errors[item.key] ? "border-danger" : ""
                            }`}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        />
                      )}
                      <ButtonCustom
                        onClick={handleSaveClick}
                        className="btn btn-primary"
                        context={t("Save")}
                        type="button"
                      />
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <button
                        onClick={handleCancelClick}
                        className="btn btn-link cancel-text d-flex align-items-center justify-content-end"
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="heading-h6">{item.label}</label>
                    <span className="text-muted d-block">
                      {item.value || ""}
                    </span>
                  </div>
                  <button
                    onClick={() => handleEditClick(item.key, item.value)}
                    className="btn btn-link"
                  >
                    {t("Edit")}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="footer-section">
          {!editingKey?.length && isEdit && (
            <ButtonCustom
              onClick={saveUserProfile}
              className="btn-primary"
              context={t("Save")}
              type="button"
            />
          )}
        </div>
      </div>
    </MainHome>
  );
};

export default UserProfile;
